import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { tileService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { MAX_PDF_FILE_SIZE } from "./../../../shared/constant";

const TileList = () => {
  const fileInputRef = useRef(null);
  
  // Add responsive styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 768px) {
        .modal-dialog {
          margin: 0.5rem;
          max-width: calc(100% - 1rem);
        }
        .modal-content {
          max-height: calc(100vh - 1rem);
          overflow-y: auto;
        }
        .form-control-sm {
          font-size: 0.875rem;
          padding: 0.375rem 0.5rem;
        }
        .btn-sm {
          font-size: 0.875rem;
          padding: 0.375rem 0.75rem;
        }
        .input-group-text {
          font-size: 0.75rem;
          padding: 0.375rem 0.5rem;
        }
      }
      @media (max-width: 576px) {
        .modal-dialog {
          margin: 0.25rem;
          max-width: calc(100% - 0.5rem);
        }
        .modal-content {
          max-height: calc(100vh - 0.5rem);
        }
        .modal-header {
          padding: 0.75rem;
        }
        .modal-body {
          padding: 0.5rem;
        }
        .modal-footer {
          padding: 0.75rem;
        }
        .card-body {
          padding: 0.75rem;
        }
        .btn {
          font-size: 0.8rem;
          padding: 0.5rem 0.75rem;
        }
        .form-control {
          font-size: 0.8rem;
          padding: 0.5rem;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [tile, setTile] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [defaultFileName, setDefaultFileName] = useState("");
  const [hideFirm, setHideFirm] = useState(false);
  const [fileName, setFileName] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 10,
  });
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [selectedTileForUnified, setSelectedTileForUnified] = useState(null);
  const [activeTab, setActiveTab] = useState("pdf");
  const [linkUrl, setLinkUrl] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const fetchTiles = async () => {
    try {
      setTableLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...filters,
      };
      const response = await tileService.getList(params);
      const { count, results } = response;
      const products = results?.products || [];
      setTiles(products);
      setPagination((prev) => ({
        ...prev,
        total_records: count,
        total_pages: Math.ceil(count / prev.per_page),
        current_page: params.page,
      }));
    } catch (error) {
      console.error("Error fetching tiles:", error);
      toast.error("Failed to load tiles");
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  };

  const handleDelete = async (tileId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the tile.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await tileService.delete(tileId);
        toast.success(response.message);
        await Swal.fire("Deleted!", "The tile has been deleted.", "success");
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        await fetchTiles();
      } catch (error) {
        console.error("Delete error:", error);
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to update publish status";
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setFilters((prev) => ({
            ...prev,
            search: searchTerm,
          }));
          setPagination((prev) => ({
            ...prev,
            current_page: 1,
          }));
        }, 500);
      };
    })(),
    []
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "search") {
      debouncedSearch(value);
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
      setPagination((prev) => ({
        ...prev,
        current_page: 1,
      }));
    }
  };

  const handleClearSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
    }));
    setPagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  const confirmPublishToggle = (tileId, action) => {
    Swal.fire({
      title: "Are you sure?",
      text: `This action will ${action} the tile`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, " + action + " it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handlePublishToggle(tileId, action);
      }
    });
  };

  const handlePublishToggle = async (tileId, action) => {
    try {
      const response = await tileService.updatePublishStatus(tileId, {
        action: action,
      });
      toast.success(response.message);
      fetchTiles();
    } catch (error) {
      console.error("Error updating publish status:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update publish status";

      toast.error(errorMsg);
    }
  };

  const handlePDFFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type !== "application/pdf") {
          toast.error(`File "${file.name}" is not a valid PDF file`);
          continue;
        }
        const maxSize = MAX_PDF_FILE_SIZE * 1024 * 1024;
        if (file.size > maxSize) {
          toast.error(
            `File "${file.name}" size should be less than ${MAX_PDF_FILE_SIZE} MB`
          );
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        setSelectedFile(validFiles[0]);
      } else {
        setSelectedFile(null);
        e.target.value = "";
      }
    }
  };

  const resetPDFFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    setFileName("");
    setSelectedFile(null);
  };

  const savePDFUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a PDF file to upload");
      return;
    }
    if (!fileName) {
      toast.error("Please enter a file name");
      return;
    }
    try {
      setUploading(true);
      const user = sessionStorage.getItem("user");
      const profile = JSON.parse(user);
      const tileData = selectedTileForUnified;
      let pdfName;
      
      if (tileData && tileData.isReplacing) {
        // Check payload type to determine naming format
        if (tileData.payload && tileData.payload.type === "include_default") {
          pdfName = (defaultFileName + " " + fileName + " " + profile.seller_profile.id).replace(/\s+/g, '_') + ".pdf";
        } else {
          pdfName = (fileName + " " + profile.seller_profile.id).replace(/\s+/g, '_') + ".pdf";
        }
      } else {
        pdfName = defaultFileName + " " + fileName + " " + profile.seller_profile.id + ".pdf";
      }
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", pdfName);
      formData.append("firm_name_hidden", hideFirm);
      let result;
      if (tileData && tileData.pdfId && tileData.isReplacing) {
        result = await tileService.replacePdf(
          tileData.product_id,
          tileData.pdfId,
          formData
        );
        console.log("PDF replace response:", result);
        if (result.status == "success") {
          if (result.pdf_name) {
            setFileName(result.pdf_name.replace('.pdf', '')); 
          }
          if (result.firm_name_hidden !== undefined) {
            setHideFirm(result.firm_name_hidden);
          }
          if (result.pdf_url) {
            setSelectedTileForUnified(prev => ({
              ...prev,
              pdf_url: result.pdf_url,
              pdf_name: result.pdf_name,
              firm_name_hidden: result.firm_name_hidden
            }));
          }
            try {
            await tileService.updatePublishStatus(tileData.product_id, {
              action: "publish",
            });
            toast.success("PDF replaced and tile published successfully");
          } catch (publishError) {
            console.error("Error publishing tile after PDF replacement:", publishError);
            toast.success("PDF replaced successfully, but failed to publish tile");
          }
          
          setTimeout(() => {
            handleCloseUnifiedModal();
          }, 1500);
        } else {
          toast.error(result.message || "Failed to replace PDF");
        }
      } else {
        result = await tileService.uploadPdf(tileData.product_id, formData);
        if (result.status == "success") {
          try {
            await tileService.updatePublishStatus(tileData.product_id, {
              action: "publish",
            });
            toast.success("PDF uploaded and tile published successfully");
          } catch (publishError) {
            console.error("Error publishing tile:", publishError);
            toast.success(
              "PDF uploaded successfully, but failed to publish tile"
            );
          }
          handleCloseUnifiedModal();
        } else {
          toast.error(result.message);
        }
      }
      setUploading(false);
      resetPDFFileInput();
      fetchTiles();
    } catch (error) {
      let message =
        selectedTileForUnified && selectedTileForUnified.pdfId && selectedTileForUnified.isReplacing
          ? "Failed to replace PDF. Please try again."
          : "Failed to upload PDF. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.name.length > 0
            ? error.response.data.name[0]
            : message;
      }
      toast.error(message);
      console.error("Error in PDF operation:", error);
      setUploading(false);
    } finally {
      setUploading(false);
    }
  };

  const handlePDFReplace = async (pdfId, productId, payload = {}) => {
    const result = await Swal.fire({
      title: "Replace PDF?",
      text: "This action will replace the existing PDF with a new one.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, replace it!",
    });
    if (result.isConfirmed) {
      try {
        const pdfDetail = await tileService.getPdfDetail(productId, pdfId);        
        const tileData = tiles.find((tile) => tile.product_id === productId);

        if (tileData && pdfDetail) {
          if (pdfDetail.pdf_name) {
            const fullName = pdfDetail.pdf_name.replace('.pdf', '');
            const user = sessionStorage.getItem("user");
            const profile = JSON.parse(user);
            const sellerId = profile.seller_profile.id;
            
            // Extract custom name by removing seller ID and default pattern
            let customName = fullName.replace(new RegExp(` ${sellerId}$`), '');
            const defaultPattern = `${tileData.tile_type} ${tileData.tile_size} ${tileData.tile_grade} ${profile.seller_profile.firm_name} ${tileData.name}`;
            
            if (customName.startsWith(defaultPattern)) {
              customName = customName.substring(defaultPattern.length).trim();
            }

            let defName = tileData.tile_type +
                " " +
                tileData.tile_size +
                " " +
                tileData.tile_grade;
                if (!pdfDetail.firm_name_hidden) {
                  defName = defName + " " + profile.seller_profile.firm_name;
                }
              let modifiedString = defName.replace(/ /g, "_");
              modifiedString = modifiedString + "_";
                
                if (customName.includes(modifiedString)) {
                  customName = customName.replace(modifiedString, "");
                }
                if (customName.includes('_' + profile.seller_profile.id)) {
                  customName = customName.replace("_"+profile.seller_profile.id, "");
                }
            // Set up the same way as first upload
            setDefaultFileName(defName +
                " " +
                tileData.name
            );
            setFileName(customName || '');
          }
          if (pdfDetail.firm_name_hidden !== undefined) {
            setHideFirm(pdfDetail.firm_name_hidden);
          }
                    setSelectedTileForUnified({
            ...tileData,
            pdfId,
            productId,
            ...pdfDetail,
            isReplacing: true,
            payload: payload, // Add payload to track naming type
          });
          setActiveTab("pdf");
          const user = sessionStorage.getItem("user");
          const profile = JSON.parse(user);
          setDefaultFileName(
            tileData.tile_type +
              " " +
              tileData.tile_size +
              " " +
              tileData.tile_grade +
              " " +
              profile.seller_profile.firm_name +
              " " +
              tileData.name
          );
          setShowUnifiedModal(true);
          // Ensure defaultFileName is updated after state changes
          setTimeout(() => {
            updateDefaultFileName();
          }, 0);
        } else {
          toast.error("Failed to load PDF details for replacement");
        }
      } catch (error) {
        console.error("Error getting PDF details:", error);
        toast.error("Failed to get PDF details. Please try again.");
      }
    }
  };

  const updateDefaultFileName = () => {
    if (selectedTileForUnified) {
      const user = sessionStorage.getItem("user");
      const profile = JSON.parse(user);
      const firmName = hideFirm ? "" : profile.seller_profile.firm_name + " ";
      setDefaultFileName(
        selectedTileForUnified.tile_type +
          " " +
          selectedTileForUnified.tile_size +
          " " +
          selectedTileForUnified.tile_grade +
          " " +
          firmName +
          selectedTileForUnified.name
      );
    }
  };

  const handleLinkReplace = async (tileData) => {
    const result = await Swal.fire({
      title: "Replace Link?",
      text: "This action will replace the existing link with a new one.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, replace it!",
    });
    if (result.isConfirmed) {
      setSelectedTileForUnified(tileData);
      setLinkUrl(tileData.link || "");
      setActiveTab("link");
      setShowUnifiedModal(true);
    }
  };

  const saveLink = async () => {
    if (!linkUrl.trim()) {
      toast.error("Please enter a valid link URL");
      return;
    }
    try {
      new URL(linkUrl);
    } catch {
      toast.error("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    try {
      setUploading(true);
      const tileData = selectedTileForUnified;
      const result = await tileService.addUpdateLink(tileData.product_id, {
        link: linkUrl.trim(),
      });
      try {
        await tileService.updatePublishStatus(tileData.product_id, {
          action: "publish",
        });
        toast.success("Link saved and tile published successfully");
      } catch (publishError) {
        console.error("Error publishing tile:", publishError);
        toast.success("Link saved successfully, but failed to publish tile");
      }
      handleCloseUnifiedModal();
      fetchTiles();
    } catch (error) {
      console.error("Error saving link:", error);
      console.error("Error response:", error?.response);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save link";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLink = async (tileId) => {
    const result = await Swal.fire({
      title: "Delete Link?",
      text: "This action will permanently delete the link.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await tileService.deleteLink(tileId);
        toast.success(response.message || "Link deleted successfully");
        fetchTiles();
      } catch (error) {
        console.error("Error deleting link:", error);
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to delete link";
        toast.error(errorMsg);
      }
    }
  };

  const handleCloseUnifiedModal = () => {
    setShowUnifiedModal(false);
    setSelectedTileForUnified(null);
    setActiveTab("");
    setFileName("");
    setLinkUrl("");
    setSelectedFile(null);
    setHideFirm(false);
    resetPDFFileInput();
  };

  const handleAddPDFLinkClick = (tileData) => {
    setSelectedTileForUnified(tileData);
    setActiveTab("");
    setShowUnifiedModal(true);
    const user = sessionStorage.getItem("user");
    const profile = JSON.parse(user);
    setDefaultFileName(
      tileData.tile_type +
        " " +
        tileData.tile_size +
        " " +
        tileData.tile_grade +
        " " +
        profile.seller_profile.firm_name +
        " " +
        tileData.name
    );
    setFileName("");
    setLinkUrl("");
    setSelectedFile(null);
    setHideFirm(false);
    // Ensure defaultFileName is updated after state changes
    setTimeout(() => {
      updateDefaultFileName();
    }, 0);
  };

  useEffect(() => {
    updateDefaultFileName();
  }, [hideFirm]);

  useEffect(() => {
    if (selectedTileForUnified && showUnifiedModal) {
      updateDefaultFileName();
    }
  }, [selectedTileForUnified, showUnifiedModal]);

  useEffect(() => {
    fetchTiles();
  }, [pagination.current_page, filters]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Tile Products Management</h4>
            <div className="card-actions">
              <Link to="/products/tiles/create" className="btn btn-primary">
                Add New Tile
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search tiles..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            {/* Tiles Table */}
            <div className="table-responsive">
              {tableLoading ? (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading...</span>
                </div>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Company Name</th>
                      <th>Grade</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>pdf/link</th>
                      <th>Published</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiles.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <p>
                              {filters.search
                                ? `No tiles found for "${filters.search}"`
                                : "No tiles found"}
                            </p>
                            {filters.search && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleClearSearch}
                              >
                                Clear Search
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      tiles.map((tile, index) => (
                        <tr key={index}>
                          <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                          <td>
                            <div>
                              <strong>{tile.manufacturing_company_name}</strong>
                            </div>
                          </td>
                          <td>
                            <strong className="small">
                              {tile.tile_grade || "N/A"}
                            </strong>
                          </td>
                          <td>
                            <strong className="small">
                              {tile.tile_type || "N/A"}
                            </strong>
                          </td>
                          <td>
                            <strong className="small">
                              {tile.tile_size || "N/A"}
                            </strong>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {/* PDF Actions - Only show if PDF data exists */}
                              {tile.pdf_list && tile.pdf_list.length > 0 && (
                                <div className="me-2">
                                  <Link
                                    className="d-inline-block me-2"
                                    to={tile.pdf_list[0].url}
                                    target="_blank"
                                  >
                                    <i className="fas fa-file-pdf me-1"></i>
                                    {tile.pdf_list[0].pdf_name || "PDF"}
                                  </Link>
                                  <i
                                    title="Replace PDF"
                                    className="pointer fas fa-sync text-primary fa-lg"
                                    onClick={() =>
                                      handlePDFReplace(
                                        tile.pdf_list[0].id,
                                        tile.product_id,
                                        { type: "include_default" } // Pass payload to control naming format
                                      )
                                    }
                                  ></i>
                                </div>
                              )}
                              {/* Link Actions - Only show if link data exists */}
                              {tile.link && (
                                <div className="me-2">
                                  <a
                                    className="d-inline-block me-2"
                                    href={tile.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="fas fa-external-link-alt me-1"></i>
                                    {tile.link || "Link"}
                                  </a>
                                  <i
                                    title="Replace Link"
                                    className="pointer fas fa-sync text-primary fa-lg me-2"
                                    onClick={() => handleLinkReplace(tile)}
                                  ></i>
                                  {/* <i
                                    title="Delete Link"
                                    className="pointer fas fa-trash text-danger fa-lg"
                                    onClick={() =>
                                      handleDeleteLink(tile.product_id)
                                    }
                                  ></i> */}
                                </div>
                              )}
                              {/* Fallback - Show when neither PDF nor link exists */}
                              {(!tile.pdf_list || tile.pdf_list.length === 0) &&
                                !tile.link && (
                                  <div className="me-2">
                                    <span
                                      title="Add PDF/Link"
                                      className="pointer text-primary"
                                      onClick={() =>
                                        handleAddPDFLinkClick(tile)
                                      }
                                    >
                                      <i className="fas fa-plus me-1"></i>
                                      Add PDF/Link
                                    </span>
                                  </div>
                                )}
                            </div>
                          </td>
                          <td>
                            <strong className="small">
                              {tile.is_published ? "Published" : "Draft"}
                            </strong>
                          </td>
                          <td>
                            <div
                              className="d-flex align-items-center gap-2"
                              role="group"
                            >
                              <div>
                                <button
                                  className={`btn btn-sm me-2 ${
                                    tile.is_published
                                      ? "btn-warning"
                                      : "btn-success"
                                  }`}
                                  onClick={() =>
                                    confirmPublishToggle(
                                      tile.product_id,
                                      tile.is_published
                                        ? "unpublish"
                                        : "publish"
                                    )
                                  }
                                >
                                  <i
                                    className={`fas fa-${
                                      tile.is_published
                                        ? "times-circle"
                                        : "check-circle"
                                    } me-1`}
                                  />
                                  {tile.is_published ? "Unpublish" : "Publish"}
                                </button>
                              </div>
                              <div>
                                <Link
                                  to={`/products/tiles/${tile.product_id}/edit`}
                                >
                                  <i className="pointer fas fa-edit text-primary me-2 fa-lg"></i>
                                </Link>
                              </div>
                              <div>
                                <i
                                  className="pointer fas fa-trash text-danger fa-lg"
                                  onClick={() => handleDelete(tile.product_id)}
                                ></i>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-3">
                <nav>
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        pagination.current_page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.current_page - 1)
                        }
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from(
                      { length: pagination.total_pages },
                      (_, i) => i + 1
                    ).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          page === pagination.current_page ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        pagination.current_page === pagination.total_pages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.current_page + 1)
                        }
                        disabled={
                          pagination.current_page === pagination.total_pages
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing{" "}
                    {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total_records
                    )}{" "}
                    of {pagination.total_records} entries
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unified PDF/Link Modal */}
      {showUnifiedModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-xl border-0" style={{ borderRadius: '12px' }}>
              <div className="modal-header bg-gradient-primary text-white border-0" style={{ 
                borderRadius: '12px 12px 0 0',
                background: '#006666'
              }}>
                <div className="d-flex align-items-center flex-grow-1">
                  <div className="me-2 me-md-3">
                    <i className="fas fa-cloud-upload-alt fa-lg"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="modal-title text-white mb-0 fw-bold fs-6 fs-md-5">
                      {selectedTileForUnified?.pdfId
                        ? "Replace PDF"
                        : selectedTileForUnified?.link
                        ? "Update Link"
                        : "Add Resource"}
                    </h5>
                    <small className="opacity-75 d-none d-md-block">
                      {selectedTileForUnified?.manufacturing_company_name}
                    </small>
                    <small className="opacity-75 d-block d-md-none text-truncate" style={{ maxWidth: '200px' }}>
                      {selectedTileForUnified?.manufacturing_company_name}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white flex-shrink-0"
                  onClick={handleCloseUnifiedModal}
                ></button>
              </div>
              <div className="modal-body p-2 p-md-4">
                <div className="row">
                  <div className="col-12">
                    {/* Tab Navigation */}
                    <div className="d-flex mb-3 mb-md-4" style={{ 
                      borderBottom: '2px solid #e3f2fd',
                      backgroundColor: '#fafafa',
                      borderRadius: '12px 12px 0 0',
                      padding: '4px 4px 0 4px'
                    }}>
                      <button
                        type="button"
                        className={`btn btn-link text-decoration-none px-2 px-md-4 py-2 py-md-3 me-1 me-md-2 flex-grow-1 flex-md-grow-0 ${
                          activeTab === "pdf" || activeTab === ""
                            ? "fw-semibold"
                            : ""
                        }`}
                        onClick={() => setActiveTab("pdf")}
                        style={{
                          background: activeTab === "pdf" || activeTab === "" 
                            ? '#006666' 
                            : 'transparent',
                          color: activeTab === "pdf" || activeTab === "" ? '#ffffff' : '#6c757d',
                          borderRadius: '8px 8px 0 0',
                          border: 'none',
                          boxShadow: activeTab === "pdf" || activeTab === "" ? '0 2px 8px rgba(33, 150, 243, 0.3)' : 'none',
                          transition: 'all 0.3s ease',
                          fontSize: '0.875rem'
                        }}
                      >
                        <i className="fas fa-upload me-1 me-md-2" style={{ 
                          color: activeTab === "pdf" || activeTab === "" ? '#ffffff' : '#6c757d' 
                        }}></i>
                        <span className="d-none d-sm-inline">PDF Upload</span>
                        <span className="d-inline d-sm-none">PDF</span>
                      </button>
                      <button
                        type="button"
                        className={`btn btn-link text-decoration-none px-2 px-md-4 py-2 py-md-3 flex-grow-1 flex-md-grow-0 ${
                          activeTab === "link"
                            ? "fw-semibold"
                            : ""
                        }`}
                        onClick={() => setActiveTab("link")}
                        style={{
                          background: activeTab === "link" 
                            ? '#006666' 
                            : 'transparent',
                          color: activeTab === "link" ? '#ffffff' : '#6c757d',
                          borderRadius: '8px 8px 0 0',
                          border: 'none',
                          boxShadow: activeTab === "link" ? '0 2px 8px rgba(76, 175, 80, 0.3)' : 'none',
                          transition: 'all 0.3s ease',
                          fontSize: '0.875rem'
                        }}
                      >
                        <i className="fas fa-link me-1 me-md-2" style={{ 
                          color: activeTab === "link" ? '#ffffff' : '#6c757d' 
                        }}></i>
                        <span className="d-none d-sm-inline">Link</span>
                        <span className="d-inline d-sm-none">Link</span>
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content">
                      {/* PDF Upload Tab */}
                      <div className={`tab-pane ${activeTab === "pdf" || activeTab === "" ? "show active" : ""}`}>
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                      <div className="card-header bg-light border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                        <h6 className="mb-0 fw-semibold fs-6">
                          <i className="fas fa-file-pdf me-1 me-md-2"></i>
                          <span className="d-none d-sm-inline">PDF Upload</span>
                          <span className="d-inline d-sm-none">PDF</span>
                        </h6>
                        <small className="text-muted d-none d-md-block">Upload a PDF document to your resource</small>
                        <small className="text-muted d-block d-md-none">Upload PDF document</small>
                      </div>
                      <div className="card-body p-2 p-md-4">
                        <div className="form-group mb-3 mb-md-4">
                          <label className="form-label fw-medium text-dark fs-6">PDF File</label>
                          <div className="input-group">
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="form-control form-control-sm form-control-md"
                              id="pdf-files"
                              accept=".pdf"
                              onChange={handlePDFFileChange}
                              onClick={() => setActiveTab("pdf")}
                              disabled={activeTab === "link" && !selectedTileForUnified?.pdfId && !selectedTileForUnified?.link}
                              required
                            />
                          </div>
                          <small className="form-text text-muted mt-1 mt-md-2 d-block small">
                            <i className="fas fa-info-circle me-1"></i>
                            <span className="d-none d-sm-inline">Supported formats: PDF (Max size: {MAX_PDF_FILE_SIZE}MB per file)</span>
                            <span className="d-inline d-sm-none">PDF (Max: {MAX_PDF_FILE_SIZE}MB)</span>
                          </small>
                        </div>
                        <div className="form-group mb-3 mb-md-4">
                          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-2">
                            <label htmlFor="fileName" className="fw-medium text-dark mb-0 mb-md-0 fs-6">
                              File Name
                            </label>
                            <div className="form-check mt-1 mt-md-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={hideFirm}
                                onChange={() => setHideFirm(!hideFirm)}
                                id="hideFirm"
                              />
                              <label className="form-check-label small text-muted" htmlFor="hideFirm">
                                Hide Firm Name
                              </label>
                            </div>
                          </div>
                          <div className="input-group">
                            <span className="input-group-text d-none d-md-inline-block bg-light text-muted small">
                              {defaultFileName}
                            </span>
                            <span className="input-group-text d-inline-block d-md-none bg-light text-muted small" style={{ fontSize: '0.7rem', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {defaultFileName ? (defaultFileName.length > 20 ? defaultFileName.substring(0, 20) + '...' : defaultFileName) : "Default filename..."}
                            </span>
                            <input
                              type="text"
                              className="form-control form-control-sm form-control-md"
                              name="fileName"
                              value={fileName}
                              id="fileName"
                              placeholder="Enter file name"
                              onChange={(e) => setFileName(e.target.value)}
                              onClick={() => setActiveTab("pdf")}
                              disabled={activeTab === "link" && !selectedTileForUnified?.pdfId && !selectedTileForUnified?.link}
                              required
                            />
                          </div>
                        </div>
                      </div>
                     </div>
                      </div>

                      {/* Link Tab */}
                      <div className={`tab-pane ${activeTab === "link" ? "show active" : ""}`}>
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                          <div className="card-header bg-light border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <h6 className="mb-0 fw-semibold fs-6">
                              <i className="fas fa-external-link-alt me-1 me-md-2"></i>
                              <span className="d-none d-sm-inline">Add Link</span>
                              <span className="d-inline d-sm-none">Link</span>
                            </h6>
                            <small className="text-muted d-none d-md-block">Add a link to your resource</small>
                            <small className="text-muted d-block d-md-none">Add link</small>
                          </div>
                          <div className="card-body p-2 p-md-4">
                            <div className="form-group mb-3">
                              <label className="form-label fw-medium text-dark fs-6">Link URL</label>
                              <input
                                type="url"
                                className="form-control form-control-sm form-control-md"
                                placeholder="https://example.com"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onClick={() => setActiveTab("link")}
                                disabled={activeTab === "pdf" && !selectedTileForUnified?.pdfId && !selectedTileForUnified?.link}
                                required
                              />
                              <small className="form-text text-muted mt-1 mt-md-2 d-block small">
                                <i className="fas fa-info-circle me-1"></i>
                                <span className="d-none d-sm-inline">Enter a valid URL (e.g., https://example.com)</span>
                                <span className="d-inline d-sm-none">Enter valid URL</span>
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light border-0 p-2 p-md-4" style={{ borderRadius: '0 0 12px 12px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-end gap-2 gap-md-3 w-100">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3 px-md-4 order-2 order-md-1"
                    onClick={handleCloseUnifiedModal}
                  >
                    <i className="fas fa-times me-1 me-md-2"></i>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={activeTab === "pdf" ? savePDFUpload : saveLink}
                    className="btn btn-primary px-3 px-md-4 fw-medium order-1 order-md-2"
                    disabled={
                      uploading ||
                      !activeTab ||
                      (activeTab === "pdf"
                        ? !fileName || !selectedFile
                        : !linkUrl.trim())
                    }
                    style={{ 
                      background: '#006666',
                      border: 'none',
                      borderRadius: '6px'
                    }}
                  >
                    {uploading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1 me-md-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span className="d-none d-sm-inline">{activeTab === "pdf" ? "Uploading..." : "Saving..."}</span>
                        <span className="d-inline d-sm-none">{activeTab === "pdf" ? "Upload..." : "Save..."}</span>
                      </>
                    ) : !activeTab ? (
                      <>
                      <i className="fas fa-upload me-1 me-md-2"></i>
                          <span className="d-none d-sm-inline">Upload PDF and Publish</span>
                          <span className="d-inline d-sm-none">Upload PDF</span>
                      </>
                    ) : activeTab === "pdf" ? (
                      selectedTileForUnified?.pdfId ? (
                        <>
                          <i className="fas fa-sync me-1 me-md-2"></i>
                          <span className="d-none d-sm-inline">Replace PDF</span>
                          <span className="d-inline d-sm-none">Replace</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload me-1 me-md-2"></i>
                          <span className="d-none d-sm-inline">Upload PDF and Publish</span>
                          <span className="d-inline d-sm-none">Upload PDF</span>
                        </>
                      )
                    ) : selectedTileForUnified?.link ? (
                      <>
                        <i className="fas fa-save me-1 me-md-2"></i>
                        <span className="d-none d-sm-inline">Update Link</span>
                        <span className="d-inline d-sm-none">Update</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-link me-1 me-md-2"></i>
                        <span className="d-none d-sm-inline">Add Link and Publish</span>
                        <span className="d-inline d-sm-none">Add Link</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUnifiedModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default TileList;
